import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, XCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { BrutalButton } from "./Brutal";
import CancelSaleDialog from "./CancelSaleDialog";
import MarkPaidDialog from "./MarkPaidDialog";

/**
 * Dashboard receivables widget — open + paid A/R rows + per-distributor
 * summary tiles, plus inline Mark-Paid / Unpay and red Cancel-Sale buttons.
 *
 * Props:
 *  - t:               i18next translator
 *  - onPaymentChange: () => void — fired after mark-paid / unpay / cancel
 *                       so the dashboard can refresh RevenueWidget etc.
 */
export default function ReceivablesCard({ t, onPaymentChange }) {
  const { user } = useAuth();
  const canCancelSales =
    !!user && user !== false && ["owner", "admin", "cfo"].includes(user.role);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [payTarget, setPayTarget] = useState(null); // open-receivable being paid
  const [cancelTarget, setCancelTarget] = useState(null); // sale being cancelled

  const load = async () => {
    try {
      const { data } = await api.get("/receivables");
      setData(data);
    } catch (e) {
      // silent — receivables widget is optional
    }
  };
  useEffect(() => {
    load();
    const tm = setInterval(load, 15000);
    return () => clearInterval(tm);
  }, []);

  if (!data) return null;
  const hasAny = data.open.length > 0 || data.paid.length > 0;
  if (!hasAny) return null;

  const fmt$ = (n) =>
    `$${Number(n || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  // Closing a receivable opens a dialog so the owner can capture the
  // distributor's actual per-unit sell price + cash collected. Re-opening
  // (paid → unpaid) is a no-prompt instant toggle.
  const onActionClick = (r) => {
    if (r.paid) {
      togglePaid(r.id);
    } else {
      setPayTarget(r);
    }
  };

  const togglePaid = async (id, body = null) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post(`/receivables/${id}/mark-paid`, body || {});
      await load();
      toast.success(t("receivables.updated"));
      setPayTarget(null);
      if (typeof onPaymentChange === "function") onPaymentChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const rows = expanded ? [...data.open, ...data.paid] : data.open;

  return (
    <section data-testid="receivables-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <Link
            to="/receivables"
            className="inline-flex items-center gap-1.5 text-zinc-950 hover:text-zinc-600 transition-colors group"
            data-testid="receivables-card-link"
          >
            <h2 className="text-lg sm:text-xl font-semibold tracking-tight">
              {t("receivables.title")}
            </h2>
            <ArrowRight
              size={14}
              weight="bold"
              className="text-zinc-400 group-hover:translate-x-0.5 transition-transform"
            />
          </Link>
          <p className="text-xs sm:text-sm text-zinc-600 mt-0.5">
            {t("receivables.subtitle", {
              open: fmt$(data.open_total),
              paid: fmt$(data.paid_total),
            })}
          </p>
        </div>
        {data.paid.length > 0 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-xs font-medium text-[#8a6a1c] hover:underline"
            data-testid="receivables-toggle-paid"
          >
            {expanded
              ? t("receivables.hide_paid")
              : t("receivables.show_paid", { n: data.paid.length })}
          </button>
        )}
      </div>

      {data.by_distributor.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
          {data.by_distributor.map((d) => (
            <div key={d.id} className="bg-white border border-zinc-200 rounded-xl p-4">
              <div className="text-sm font-semibold truncate">{d.name}</div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">{d.email}</div>
              <div className="mt-2 flex items-baseline gap-2">
                <span
                  className="text-xl font-semibold text-zinc-900"
                  data-testid={`open-amount-${d.id}`}
                >
                  {fmt$(d.open_amount)}
                </span>
                <span className="text-xs text-zinc-500">
                  {t("receivables.n_open", { count: d.open_count })}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-700">
          {t("receivables.all_settled")}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`receivable-${r.id}`}
              className={`bg-white border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
                r.paid ? "border-zinc-200 opacity-60" : "border-zinc-200"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-semibold text-zinc-900 truncate">{r.product_name}</span>
                  <span className="text-xs text-zinc-500">
                    × {r.quantity} {r.unit}
                  </span>
                  {r.paid && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">
                      {t("receivables.paid_pill")}
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">{r.to_user_name}</div>
                {r.note && (
                  <div
                    className="text-xs italic text-zinc-500 mt-0.5 truncate"
                    title={r.note}
                    data-testid={`receivable-note-${r.id}`}
                  >
                    &ldquo;{r.note}&rdquo;
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between gap-3 sm:justify-end shrink-0">
                <span
                  className="text-lg font-semibold text-zinc-900 sm:order-none"
                  data-testid={`amount-${r.id}`}
                >
                  {fmt$(r.amount)}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  <BrutalButton
                    size="sm"
                    variant={r.paid ? "secondary" : "primary"}
                    onClick={() => onActionClick(r)}
                    disabled={!!busy[r.id]}
                    data-testid={`mark-paid-${r.id}`}
                  >
                    {r.paid ? t("receivables.mark_unpaid") : t("receivables.mark_paid")}
                  </BrutalButton>
                  {canCancelSales && (
                    <BrutalButton
                      size="sm"
                      variant="secondary"
                      onClick={() => setCancelTarget(r)}
                      disabled={!!busy[r.id]}
                      data-testid={`cancel-sale-receivable-${r.id}`}
                      className="!border-rose-300 !text-rose-700 hover:!bg-rose-50 !px-2"
                      title={t("cancel_sale.button")}
                    >
                      <XCircle size={14} weight="bold" />
                    </BrutalButton>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {payTarget && (
        <MarkPaidDialog
          target={payTarget}
          busy={!!busy[payTarget.id]}
          onCancel={() => setPayTarget(null)}
          onConfirm={(body) => togglePaid(payTarget.id, body)}
          t={t}
        />
      )}

      {cancelTarget && (
        <CancelSaleDialog
          sale={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onCancelled={async () => {
            await load();
            if (typeof onPaymentChange === "function") onPaymentChange();
          }}
        />
      )}
    </section>
  );
}
